import type { CSSProperties, ImgHTMLAttributes } from "react";
import ReactCountryFlag from "react-country-flag";

interface Props extends Omit<ImgHTMLAttributes<HTMLImageElement>, "alt" | "role"> {
    countryCode: string;
    /** The country is already named in text next to the flag: hide the image from assistive technology. */
    decorative?: boolean;
    style?: CSSProperties;
}

let regionNames: Intl.DisplayNames | null | undefined;

/** "US" -> "United States"; falls back to the code itself for anything Intl does not know. */
export const countryName = (code: string): string => {
    if (!code) return "";
    if (regionNames === undefined) {
        try {
            regionNames = new Intl.DisplayNames(["en"], { type: "region" });
        } catch {
            regionNames = null;
        }
    }
    try {
        return regionNames?.of(code.toUpperCase()) ?? code;
    } catch {
        return code;
    }
};

/**
 * Whether `code` is a region the CDN can have a flag for. Visitors see scrambled rows (obfuscated
 * data), whose country codes are random letter pairs ("PX", "LN"): each became a 404 from the
 * flag CDN and a console error (/stock-stamps). Intl hands an unknown code back unchanged; where
 * Intl.DisplayNames is missing every two-letter code is taken as real.
 */
export const isKnownRegion = (code: string): boolean => {
    if (!/^[a-z]{2}$/i.test(code ?? "")) return false;
    const upper = code.toUpperCase();
    if (upper === "ZZ") return false; // "Unknown Region"
    countryName(upper); // initialises regionNames
    return regionNames === null || countryName(upper) !== upper;
};

/**
 * A country flag (an SVG <img> from the flag-icons CDN, via react-country-flag) that always has an alt:
 * the country's name, or empty with role="presentation" when the name is already written beside it.
 * Nothing for a code that is not a real region (see isKnownRegion).
 */
const CountryFlag = ({ countryCode, decorative = false, ...rest }: Props) => {
    if (!countryCode || !isKnownRegion(countryCode)) return null;
    return <ReactCountryFlag countryCode={countryCode} svg alt={decorative ? "" : countryName(countryCode)} role={decorative ? "presentation" : undefined} {...rest} />;
};

export default CountryFlag;
